/**
 * Public a11y modal helpers — native <dialog> and legacy div.modal.
 * Prefer HTMLDialogElement (showModal/close + browser inert/Escape).
 * Legacy bridge: role/aria, focus trap, Escape, focus restore.
 *
 * Pattern sources:
 * - https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog
 * - https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
 */

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Open a native <dialog> via showModal(); stores invoker for focus restore.
 * @param {HTMLDialogElement} dialog
 * @param {{ invoker?: HTMLElement|null }} [options]
 */
export function openA11yDialog(dialog, { invoker } = {}) {
  if (!(dialog instanceof HTMLDialogElement)) {
    throw new Error('Expected <dialog>');
  }
  dialog.returnValue = '';
  dialog._a11yInvoker = invoker || document.activeElement;
  if (!dialog._a11yCancelBound) {
    dialog.addEventListener('cancel', onDialogCancel);
    dialog._a11yCancelBound = true;
  }
  dialog.showModal();
  const focusTarget =
    dialog.querySelector('[data-initial-focus]') ||
    dialog.querySelector(FOCUSABLE);
  focusTarget?.focus?.();
}

/**
 * Close a native <dialog> and restore focus to the invoker.
 * @param {HTMLDialogElement} dialog
 */
export function closeA11yDialog(dialog) {
  if (!(dialog instanceof HTMLDialogElement)) return;
  const invoker = dialog._a11yInvoker;
  dialog.close();
  invoker?.focus?.();
  dialog._a11yInvoker = null;
}

function onDialogCancel(e) {
  const dialog = e.currentTarget;
  const invoker = dialog._a11yInvoker;
  queueMicrotask(() => {
    invoker?.focus?.();
    dialog._a11yInvoker = null;
  });
}

function getFocusable(el) {
  return [...el.querySelectorAll(FOCUSABLE)].filter((n) => {
    if (n.hasAttribute('disabled')) return false;
    const style = window.getComputedStyle(n);
    return style.visibility !== 'hidden' && style.display !== 'none';
  });
}

/**
 * Attach ARIA, Escape, and Tab trap without owning display/visibility.
 * Use when a consumer already manages open/close transitions (e.g. Add Item).
 *
 * @param {HTMLElement} el
 * @param {{ invoker?: HTMLElement|null, labelledBy?: string, onClose?: () => void, focus?: boolean }} [options]
 */
export function attachModalA11y(el, { invoker, labelledBy, onClose, focus = true } = {}) {
  if (!el) return;
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-hidden', 'false');
  if (labelledBy) el.setAttribute('aria-labelledby', labelledBy);
  el._a11yInvoker = invoker || el._a11yInvoker || document.activeElement;
  el._a11yOnClose = typeof onClose === 'function' ? onClose : el._a11yOnClose || null;

  if (!el._a11yEscBound) {
    el._a11yEscHandler = (e) => {
      if (e.key !== 'Escape') return;
      if (!el.classList.contains('is-visible') && el.dataset.state !== 'open') return;
      e.preventDefault();
      if (typeof el._a11yOnClose === 'function') {
        el._a11yOnClose();
      } else {
        closeLegacyModal(el);
      }
    };
    document.addEventListener('keydown', el._a11yEscHandler);
    el._a11yEscBound = true;
  }

  if (!el._a11yTrapBound) {
    el._a11yTrapHandler = (e) => {
      if (e.key !== 'Tab') return;
      if (!el.classList.contains('is-visible') && el.dataset.state !== 'open') return;
      const nodes = getFocusable(el);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', el._a11yTrapHandler);
    el._a11yTrapBound = true;
  }

  if (focus) {
    const focusTarget =
      el.querySelector('[data-initial-focus]') ||
      getFocusable(el)[0];
    focusTarget?.focus?.();
  }
}

/**
 * Remove Escape/Tab listeners and optionally restore focus.
 * @param {HTMLElement} el
 * @param {{ restoreFocus?: boolean }} [options]
 */
export function detachModalA11y(el, { restoreFocus = true } = {}) {
  if (!el) return;
  el.setAttribute('aria-hidden', 'true');
  el._a11yOnClose = null;
  if (el._a11yEscBound && el._a11yEscHandler) {
    document.removeEventListener('keydown', el._a11yEscHandler);
    el._a11yEscBound = false;
    el._a11yEscHandler = null;
  }
  if (el._a11yTrapBound && el._a11yTrapHandler) {
    document.removeEventListener('keydown', el._a11yTrapHandler);
    el._a11yTrapBound = false;
    el._a11yTrapHandler = null;
  }
  if (restoreFocus) {
    el._a11yInvoker?.focus?.();
  }
  el._a11yInvoker = null;
}

/**
 * Legacy bridge for div.modal until migrated to <dialog>.
 * Sets dialog ARIA, toggles visibility, focus trap, Escape, focuses first control.
 *
 * @param {HTMLElement} el
 * @param {{ invoker?: HTMLElement|null, labelledBy?: string, onClose?: () => void, display?: string }} [options]
 */
export function openLegacyModal(el, { invoker, labelledBy, onClose, display = 'flex' } = {}) {
  if (!el) return;
  el.classList.add('is-visible');
  el.style.display = display;
  el.dataset.state = 'open';
  attachModalA11y(el, { invoker, labelledBy, onClose, focus: true });
}

/**
 * Close a legacy div.modal and restore focus to the invoker.
 * @param {HTMLElement} el
 */
export function closeLegacyModal(el) {
  if (!el) return;
  el.classList.remove('is-visible');
  el.style.display = 'none';
  el.dataset.state = 'closed';
  detachModalA11y(el, { restoreFocus: true });
}
